<template>
  <div class="v2026-mt-row" :class="'is-' + layout">
    <div class="v2026-mt-cell-key">
      <span class="v2026-mt-key">/{{ messageTemplate.key }}</span>
    </div>

    <div class="v2026-mt-cat" :title="categoryName">
      <span class="v2026-mt-sr">หมวดหมู่: </span>{{ categoryName }}
    </div>

    <!-- lg only: Topic owns a column. At md/sm it renders as a caption inside the content cell. -->
    <div v-if="layout === 'lg'" class="v2026-mt-topic" :title="messageTemplate.topic">
      <span class="v2026-mt-sr">Topic: </span>{{ messageTemplate.topic }}
    </div>

    <div class="v2026-mt-content" :class="{ 'has-video': isVideo }">
      <div v-if="layout !== 'lg' && messageTemplate.topic" class="v2026-mt-caption" :title="messageTemplate.topic">
        <span class="v2026-mt-sr">Topic: </span>{{ messageTemplate.topic }}
      </div>

      <div class="v2026-mt-body">
        <span class="v2026-mt-sr">เนื้อหา: </span>
        <template v-if="messageTemplate.type === 'text'">
          <span v-if="messageTemplate.message" class="v2026-mt-body__text">{{ messageTemplate.message }}</span>
        </template>
        <div v-else class="v2026-mt-strip">
          <button v-for="(image, index) in messageTemplate.images"
                  :key="index"
                  type="button"
                  :aria-label="'ดูรูปที่ ' + (index + 1) + ' ขนาดเต็ม'"
                  @click="$emit('preview', image.url)">
            <img :src="image.url" height="56" alt="">
          </button>
        </div>
      </div>

      <div v-if="messageTemplate.type === 'text' && messageTemplate.attachment" class="v2026-mt-att">
        <!-- No options prop: default plyr controls, so the video stays playable as it is today. -->
        <div v-if="isVideo" class="v2026-mt-att__video">
          <VuePlyr>
            <video :src="messageTemplate.attachment" format="audio/mp4"/>
          </VuePlyr>
        </div>
        <!-- No @load, ever: v-lazy emits no Vue event and no DOM event. Its only signal is the
             lazy="loading|loaded|error" attribute, which CSS reads. -->
        <button v-else type="button" class="v2026-mt-thumb" aria-label="ดูรูปขนาดเต็ม"
                @click="$emit('preview', messageTemplate.attachment)">
          <img v-lazy="messageTemplate.attachment" alt="">
        </button>
      </div>
    </div>

    <div class="v2026-mt-actions">
      <button ref="editBtn"
              type="button"
              class="v2026-mt-edit"
              :aria-label="'แก้ไขคีย์ลัด /' + messageTemplate.key"
              @click="setIsEditing(true)">
        <span class="v2026-mt-edit__ico" aria-hidden="true"></span>แก้ไข
      </button>
    </div>

    <BaseModal :open="isEditing" maxWidth="720px" :closeOnBackdrop="false" @close="onEditModalClose">
      <div class="v2026-mte" role="dialog" aria-modal="true" aria-label="แก้ไขคีย์ลัด">
        <div class="v2026-mte__head">
          <span class="v2026-mte__title">แก้ไขคีย์ลัด</span>
          <span class="v2026-mt-key">/{{ messageTemplate.key }}</span>
          <button type="button" class="v2026-mte__x" aria-label="ปิด" @click="cancelUpdate()">×</button>
        </div>

        <div class="v2026-mte__body">
          <div>
            <label class="v2026-mte__label" :for="'v2026-mte-cat-' + messageTemplate.id">หมวดหมู่</label>
            <select :id="'v2026-mte-cat-' + messageTemplate.id"
                    ref="categorySelect"
                    name="category"
                    class="v2026-mte__select"
                    v-model="message_template_category_id">
              <option value="">กรุณาเลือกหมวดหมู่</option>
              <option v-for="(item, index) in categories" :value="item.id" :key="index">
                {{ item.name }}
              </option>
            </select>
          </div>

          <div>
            <label class="v2026-mte__label" :for="'v2026-mte-topic-' + messageTemplate.id">Topic</label>
            <input :id="'v2026-mte-topic-' + messageTemplate.id"
                   type="text"
                   class="v2026-mte__input"
                   placeholder="หัวข้อ Shortcut"
                   v-model="topic">
          </div>

          <div v-if="messageTemplate.type === 'text'">
            <label class="v2026-mte__label" :for="'v2026-mte-msg-' + messageTemplate.id">เนื้อหา</label>
            <div class="v2026-mte__row">
              <textarea :id="'v2026-mte-msg-' + messageTemplate.id"
                        rows="6"
                        class="v2026-mte__textarea"
                        placeholder="พิมพ์ข้อความ..."
                        v-model="message"/>
              <div>
                <div v-if="imgUrl" class="v2026-mte__att">
                  <button type="button"
                          class="v2026-mte__remove"
                          :aria-label="fileUploadExt === 'mp4' ? 'ลบวิดีโอ' : 'ลบรูป'"
                          @click="confirmDeleteAttachment()">×</button>
                  <div v-if="fileUploadExt === 'mp4'" class="v2026-mte__video">
                    <VuePlyr>
                      <video :src="imgUrl" format="audio/mp4"/>
                    </VuePlyr>
                  </div>
                  <div v-else class="v2026-mte__img">
                    <img :src="imgUrl" alt="">
                  </div>
                </div>
                <template v-else>
                  <button type="button" class="v2026-mte__upload" @click="clickFileUpload()">
                    <span class="v2026-mte__upload-ico" aria-hidden="true"></span>
                    เพิ่มรูปภาพ/วิดีโอ
                  </button>
                  <input type="file" ref="fileUpload" @change="handleFileUpload" hidden>
                </template>
              </div>
            </div>
            <div class="v2026-mte__hint">ไฟล์จะถูกอัปโหลดเมื่อกดตกลง</div>
          </div>

          <div v-else-if="messageTemplate.type === 'image'">
            <span class="v2026-mte__label">เนื้อหา</span>
            <vue-dropzone ref="myVueDropzone"
                          id="dropzone"
                          :destroyDropzone="false"
                          :options="dropzoneOptions"
                          @vdropzone-mounted="loadPictures"
                          @vdropzone-processing="dzChangeUrl"
                          @vdropzone-success="dzSuccess"
                          @vdropzone-removed-file="dzRemoveFile"></vue-dropzone>
            <div class="v2026-mte__hint">รูปในแกลอรี่จะถูกอัปโหลดทันทีที่วางไฟล์ และการลบจะมีผลทันที</div>
          </div>

          <div>
            <button v-if="messageTemplate.type === 'text'"
                    type="button"
                    class="v2026-mte__btn-ghost"
                    @click="setTemplateType('image')">เปลี่ยนเป็นแกลอรี่</button>
            <button v-if="messageTemplate.type === 'image'"
                    type="button"
                    class="v2026-mte__btn-ghost"
                    @click="setTemplateType('text')">เปลี่ยนเป็นข้อความ</button>
          </div>
        </div>

        <div class="v2026-mte__foot">
          <button type="button" class="v2026-mte__btn-ghost" @click="cancelUpdate()">ยกเลิก</button>
          <button type="button"
                  class="v2026-mte__btn-primary"
                  :disabled="isLoading"
                  @click="updateMessageTemplate()">
            {{ isLoading ? 'กำลังโหลด...' : 'ตกลง' }}
          </button>
        </div>
      </div>
    </BaseModal>

    <BaseModal :open="confirmOpen" maxWidth="420px" @close="confirmOpen = false">
      <div class="v2026-mtc" role="dialog" aria-modal="true" :aria-label="confirmText">
        <div class="v2026-mtc__text">{{ confirmText }}</div>
        <div class="v2026-mtc__foot">
          <button ref="confirmNo" type="button" class="v2026-mte__btn-ghost"
                  @click="confirmOpen = false">ไม่</button>
          <button type="button" class="v2026-mte__btn-danger"
                  @click="acceptDeleteAttachment()">ใช่</button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import axios from 'axios'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import BaseModal from '../coversation/BaseModal'

export default {
  name: 'message-template-item-v2026',
  props: {
    messageTemplate: Object,
    layout: {
      type: String,
      default: 'lg'
    }
  },
  components: {
    vueDropzone: vue2Dropzone,
    BaseModal
  },
  data () {
    return {
      apiUrl: 'https://api.beneat-chat.com',
      message: null,
      fileUpload: null,
      fileUploadExt: null,
      imgUrl: null,
      isEditing: false,
      isDzDestroying: false,
      dropzoneOptions: {
        url: null,
        thumbnailWidth: 150,
        maxFilesize: 0.5,
        destroyDropzone: false,
        addRemoveLinks: true,
        autoProcessQueue: true,
        dictDefaultMessage: '<i class=\'fa fa-upload\'></i> อัพโหลดไฟล์'
      },
      message_template_category_id: null,
      topic: '',
      isLoading: false,
      confirmOpen: false
    }
  },
  computed: {
    ...mapState({
      categories: state => state.messageTemplate.categories
    }),
    categoryName () {
      return this.messageTemplate.category ? this.messageTemplate.category.name : ''
    },
    isVideo () {
      return this.getFileExtension(this.messageTemplate.attachment) === 'mp4'
    },
    confirmText () {
      return (this.fileUploadExt === 'mp4') ? 'คุณต้องการลบวิดีโอใช่หรือไม่' : 'คุณต้องการลบรูปนี้ใช่หรือไม่'
    }
  },
  beforeDestroy () {
    this.isDzDestroying = true
  },
  destroyed () {
    this.isDzDestroying = false
  },
  watch: {
    isEditing () {
      if (this.isEditing) {
        this.message = this.messageTemplate.message
        this.imgUrl = this.messageTemplate.attachment
        this.fileUploadExt = this.getFileExtension(this.messageTemplate.attachment)
        this.message_template_category_id = this.messageTemplate.category ? JSON.parse(JSON.stringify(this.messageTemplate.category.id)) : ''
        this.topic = JSON.parse(JSON.stringify(this.messageTemplate.topic))
        this.$nextTick(() => {
          if (this.$refs.categorySelect) this.$refs.categorySelect.focus()
        })
      } else {
        this.$nextTick(() => {
          if (this.$refs.editBtn) this.$refs.editBtn.focus()
        })
      }
    },
    confirmOpen (isOpen) {
      if (!isOpen) return
      this.$nextTick(() => {
        if (this.$refs.confirmNo) this.$refs.confirmNo.focus()
      })
    }
  },
  methods: {
    ...mapActions('messageTemplate', ['fetchMessageTemplates', 'deleteImage']),
    // Both modals listen for Escape, so without this guard one Escape would close the confirm AND
    // discard the edit.
    onEditModalClose () {
      if (this.confirmOpen) return
      this.cancelUpdate()
    },
    dzChangeUrl (file, xhr, formData) {
      this.$refs.myVueDropzone.setOption('url', `${this.apiUrl}/message-templates/${this.messageTemplate.id}/images?uuid=${file.upload.uuid}`)
    },
    dzSuccess (file, response) {
      console.log('dz success', file, response)
    },
    dzRemoveFile (file, error, xhr) {
      if (this.isDzDestroying) {
        return
      }
      const submitData = {
        id: this.messageTemplate.id,
        uuid: (file.upload) ? file.upload.uuid : file.uuid
      }
      this.deleteImage(submitData)
    },
    setIsEditing (status) {
      this.isEditing = status
    },
    setTemplateType (type) {
      this.messageTemplate.type = type
    },
    clickFileUpload () {
      this.$refs.fileUpload.click()
    },
    handleFileUpload () {
      this.fileUpload = this.$refs.fileUpload.files[0]
      this.fileUploadExt = this.getFileExtension(this.fileUpload.name)
      this.imgUrl = URL.createObjectURL(this.fileUpload)
    },
    getFileExtension (url) {
      return url ? url.split(/[#?]/)[0].split('.').pop().trim() : null
    },
    cancelUpdate () {
      this.setIsEditing(false)
      this.message = null
      this.fileUpload = null
      this.imgUrl = null
    },
    confirmDeleteAttachment () {
      this.confirmOpen = true
    },
    async acceptDeleteAttachment () {
      this.confirmOpen = false
      try {
        await this.deleteAttachment()
      } catch (err) {
        this.$emit('notify', {
          variant: 'danger',
          title: 'ลบไฟล์แนบไม่สำเร็จ',
          message: (err && err.message) || ''
        })
      }
    },
    async deleteAttachment () {
      const url = `/message-templates/${this.messageTemplate.id}/attachment`
      const res = await axios.delete(url)
      if (res.status === 200) {
        this.imgUrl = null
        const updatedMessageTemplate = res.data
        this.$store.commit(
          'messageTemplate/UPDATE_MESSAGE_TEMPLATE',
          updatedMessageTemplate
        )
      }
    },
    async updateMessageTemplate () {
      this.isLoading = true
      let formData = new FormData()
      formData.append('type', this.messageTemplate.type)
      if (this.message) {
        formData.append('message', this.message)
      }
      if (this.message_template_category_id) {
        formData.append('message_template_category_id', this.message_template_category_id)
      }
      if (this.topic) {
        formData.append('topic', this.topic)
      }
      if (this.fileUpload) formData.append('fileUpload', this.fileUpload)
      // Update message template
      const url = (this.fileUploadExt === 'mp4') ? `/message-templates/${this.messageTemplate.id}/video` : `/message-templates/${this.messageTemplate.id}`
      try {
        const res = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        if (res.status === 200) {
          const updatedMessageTemplate = res.data
          this.$store.commit(
            'messageTemplate/UPDATE_MESSAGE_TEMPLATE',
            updatedMessageTemplate
          )

          // ให้ Fetch Data ใหม่ของ MessageBox
          this.$store.commit('messageTemplate/SET_MESSAGE_TEMPLATES', {
            messageTemplates: [],
            customerType: this.messageTemplate.customer_type
          })
          const paramsFetchMessage = {
            page: 0,
            customer_type: this.messageTemplate.customer_type,
            limit: 50,
            show_categories: 1,
            keyword: ''
          }
          this.fetchMessageTemplates(paramsFetchMessage)
          this.cancelUpdate()
        }
      } catch (err) {
        // Without this the rejected post leaves isLoading true and ตกลง disabled forever.
        this.$emit('notify', {
          variant: 'danger',
          title: 'บันทึกไม่สำเร็จ',
          message: (err && err.message) || ''
        })
      } finally {
        this.isLoading = false
      }
    },
    loadPictures () {
      if (this.messageTemplate.images) {
        this.messageTemplate.images.forEach(image => {
          const file = {
            size: 123,
            uuid: image.uuid,
            name: image.original_name,
            type: image.type,
            dataURL: image.url
          }
          this.$refs.myVueDropzone.manuallyAddFile(file, image.url)
          this.$refs.myVueDropzone.dropzone.emit('thumbnail', file, file.dataURL)
          this.$refs.myVueDropzone.dropzone.emit('complete', file)
        })
      }
    }
  },
  mounted () {
    if (window.location.hostname === 'localhost') {
      this.apiUrl = 'http://localhost:3000'
    }
    this.dropzoneOptions.url = `${this.apiUrl}/message-templates/${this.messageTemplate.id}/images`
  }
}
</script>

<style lang="scss" scoped>
.v2026-mt-row {
  display: grid;
  grid-template-columns: var(--mt-cols);
  column-gap: 16px;
  align-items: start;
  min-height: 64px;
  padding: 12px 8px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 150ms ease;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:last-child { border-bottom: 0; }

  &.is-md { column-gap: 12px; }

  &.is-sm {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      "key cat act"
      "body body body";
    align-items: center;
    column-gap: 8px;
    row-gap: 8px;
    margin: 8px 0;
    padding: 12px;
    border: 1px solid #e9ecef;
    border-radius: 8px;

    .v2026-mt-cell-key { grid-area: key; }
    .v2026-mt-cat {
      grid-area: cat;
      -webkit-line-clamp: 1;
      padding: 2px 8px;
      font-size: 12px;
      background: #F2F4F7;
      border-radius: 6px;
      justify-self: start;
    }
    .v2026-mt-content { grid-area: body; }
    .v2026-mt-actions { grid-area: act; }
  }
}

.v2026-mt-key {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  background: #F2F4F7;
  border: 1px solid #E4E7EC;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-mt-cat {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  color: #343a40;
}

.v2026-mt-topic {
  font-size: 13px;
  color: #343a40;
  overflow-wrap: anywhere;
}

.v2026-mt-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "topic att"
    "body att";
  column-gap: 12px;
  row-gap: 6px;
  min-width: 0;

  /* A 200x112 player never sits beside text — at ANY tier. This is what kills the mp4 drift. */
  &.has-video,
  .is-sm & {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "topic"
      "body"
      "att";
  }
}

.v2026-mt-caption {
  grid-area: topic;
  font-size: 12px;
  font-weight: 600;
  color: #66696C;
}

.v2026-mt-body {
  grid-area: body;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  color: #343a40;
}

.v2026-mt-body__text {
  white-space: pre-wrap;
  word-break: break-word;
}

.v2026-mt-att { grid-area: att; }

.v2026-mt-att__video {
  width: 200px;
  height: 112px;
  border-radius: 8px;
  overflow: hidden;
  background: #F2F4F7;

  ::v-deep .plyr { width: 100%; height: 100%; }
  ::v-deep .plyr video { width: 100%; height: 100%; object-fit: contain; }
}

.v2026-mt-thumb {
  display: block;
  width: 72px;
  height: 72px;
  padding: 0;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  /* 'hidden' does NOT match vue-lazyload's /(scroll|auto)/ scroll-parent probe — keep it. */
  overflow: hidden;
  background: #F2F4F7;
  cursor: pointer;

  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  img[lazy="loading"] { opacity: 0; }
  img[lazy="error"] { opacity: .4; }
}

.v2026-mt-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overscroll-behavior-x: contain;

  button {
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;

    &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  }

  img {
    display: block;
    height: 56px;
    width: auto;
    border-radius: 6px;
  }
}

.v2026-mt-actions {
  display: flex;
  justify-content: flex-end;
}

.v2026-mt-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 13px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-mt-edit__ico {
  display: inline-block;
  width: 16px;
  height: 16px;
  background-color: var(--v2026-color-icon-idle);
  -webkit-mask: url('~@/assets/icons2026/pencil-edit.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/pencil-edit.svg') center / contain no-repeat;
}

.v2026-mt-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* ── edit modal ─────────────────────────────────────────────────────────── */

.v2026-mte {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
}

.v2026-mte__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 20px;
  border-bottom: 1px solid #e9ecef;
}

.v2026-mte__title {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-mte__x {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: none;
  font-size: 18px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;

  &:hover { background: var(--v2026-color-hover-bg); color: #212529; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-mte__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: grid;
  gap: 16px;
}

.v2026-mte__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.v2026-mte__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
}

.v2026-mte__select {
  width: 100%;
  height: 36px;
  padding: 0 32px 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  appearance: none;
  -webkit-appearance: none;
  background-image: url('~@/assets/icons2026/chevron-right.svg');
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-mte__input {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;

  &::placeholder { color: #66696C; }
  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-mte__textarea {
  width: 100%;
  min-height: 132px;
  padding: 10px 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  font-size: 14px;
  color: #212529;
  resize: vertical;

  &::placeholder { color: #66696C; }
  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
}

.v2026-mte__att { position: relative; }

.v2026-mte__remove {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 1;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #E4E7EC;
  border-radius: 50%;
  background: #ffffff;
  font-size: 15px;
  line-height: 1;
  color: #E5484D;
  cursor: pointer;

  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-mte__video {
  width: 320px;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  background: #F2F4F7;

  ::v-deep .plyr { width: 100%; height: 100%; }
  ::v-deep .plyr video { width: 100%; height: 100%; object-fit: contain; }
}

.v2026-mte__img {
  width: 160px;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  background: #F2F4F7;

  img { width: 100%; height: 100%; object-fit: cover; display: block; }
}

.v2026-mte__upload {
  width: 160px;
  height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px dashed #E4E7EC;
  border-radius: 8px;
  background: #F2F4F7;
  font-size: 12px;
  color: #66696C;
  cursor: pointer;

  &:hover { border-color: var(--v2026-color-main); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-mte__upload-ico {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-color: #66696C;
  -webkit-mask: url('~@/assets/icons2026/composer-attach.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/composer-attach.svg') center / contain no-repeat;
}

.v2026-mte__hint {
  margin-top: 8px;
  font-size: 12px;
  color: #66696C;
}

.v2026-mte__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  height: 64px;
  padding: 0 20px;
  border-top: 1px solid #e9ecef;
}

.v2026-mte__btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-mte__btn-primary {
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--v2026-color-main);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(.94); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-mte__btn-danger {
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: #E5484D;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

/* ── delete confirm ─────────────────────────────────────────────────────── */

.v2026-mtc {
  padding: 24px 20px;
  background: #ffffff;
  border-radius: 24px;
}

.v2026-mtc__text {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-mtc__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

/* The dialog is viewport-sized, not column-sized, so a media query is legitimate here only. */
@media (max-width: 760px) {
  .v2026-mte__row { grid-template-columns: minmax(0, 1fr); }
  .v2026-mte__video { width: 100%; height: auto; aspect-ratio: 16 / 9; }
}

@media (prefers-reduced-motion: reduce) {
  .v2026-mt-row { transition: none; }
}
</style>
